// Ported from design/project/App23x-WeeklyXP and App23y-HighRollers, then wired: this week's two boards with the
// prizes they are on course for, and last week's final results. The player's own row is pinned above the menu, so it
// never needs a scroll to find; only the list scrolls.
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { rules } from "@degenduel/shared";
import { useGame, useTicker } from "../game.js";
import { RanksEventsToggle } from "./Tournaments.js";

type Board = "xp" | "volume";
interface Row {
  rank: number;
  playerId: string;
  name: string;
  score: string;
  prize: string;
}
interface View {
  week: { id: string; startsAt: number; endsAt: number };
  pot: string;
  top: Row[];
  me: { rank: number | null; score: string; battles: number; opponents: number; qualified: boolean; toTop10: string | null } | null;
}
interface Last {
  week: string | null;
  pot: string;
  top: Row[];
}

const UN = { fontFamily: "Unbounded, sans-serif", fontWeight: "900" } as const;
const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;
const COLOURS = ["#3DFFD0", "#8A7CFF", "#FF8A3D", "#2AABEE", "#FF3366", "#9945FF", "#F7931A", "#CCFF00", "#627EEA", "#FF5C85"];
const initials = (name: string) => name.replace(/^@/, "").slice(0, 2).toUpperCase();
const unit = (board: Board) => (board === "xp" ? " XP" : "");
const money = (s: string) => `+$${s}`;

function Podium({ top, board }: { top: Row[]; board: Board }) {
  const order = [top[1], top[0], top[2]];
  return (
    <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px", alignItems: "end" }}>
      {order.map((r, i) => {
        const gold = i === 1;
        const rank = gold ? 1 : i === 0 ? 2 : 3;
        const colour = rank === 1 ? "#CCFF00" : rank === 2 ? "#8A7CFF" : "#FF8A3D";
        const height = rank === 1 ? 124 : rank === 2 ? 96 : 80;
        if (!r) return <div key={rank} style={{ height: `${height}px`, borderRadius: "16px 16px 6px 6px", border: "1px dashed rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#4B535E", ...UN, fontSize: "18px" }}>{rank}</div>;
        return (
          <div key={rank} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", minWidth: "0" }}>
            {gold && <svg width="26" height="18" viewBox="0 0 26 18" aria-hidden="true"><path d="M2 16 L4 4 L9 9 L13 2 L17 9 L22 4 L24 16 Z" fill="#CCFF00" stroke="#0B0E14" strokeWidth="1.5" strokeLinejoin="round"></path></svg>}
            <div className="av" style={{ width: gold ? "64px" : "52px", height: gold ? "64px" : "52px", background: COLOURS[rank - 1], fontSize: gold ? "18px" : "15px", border: `3px solid ${colour}`, ...((r as { frame?: string | null }).frame && { border: "4px solid transparent", backgroundImage: `linear-gradient(${COLOURS[rank - 1]}, ${COLOURS[rank - 1]}), ${(r as { frame?: string }).frame}`, backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box" }) }}>{initials(r.name)}</div>
            <span style={{ fontSize: "12px", fontWeight: gold ? 700 : 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
            <div className={gold ? undefined : "g"} style={{ width: "100%", height: `${height}px`, boxSizing: "border-box", borderRadius: "16px 16px 6px 6px", ...(gold && { background: "linear-gradient(180deg, rgba(204,255,0,0.25), rgba(22,27,34,0.85))", border: "1px solid rgba(204,255,0,0.6)" }), display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px" }}>
              <span style={{ ...UN, fontSize: gold ? "28px" : rank === 2 ? "22px" : "20px", color: colour }}>{rank}</span>
              <span className="mono" style={{ fontSize: "11px", color: gold ? "#FFFFFF" : "#8B949E", whiteSpace: "nowrap" }}>{r.score}{unit(board)}</span>
              <span style={{ ...MONO, fontSize: "12px", color: "#CCFF00" }}>{money(r.prize)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function List({ rows, board }: { rows: Row[]; board: Board }) {
  if (!rows.length) return null;
  return (
    <div className="g" style={{ marginTop: "12px", borderRadius: "20px", padding: "4px 0" }}>
      {rows.map((r, i) => (
        <div key={r.rank}>
          {i > 0 && <div className="div" style={{ margin: "0 14px" }}></div>}
          <div className="li">
            <span className="mono muted" style={{ width: "24px", fontSize: "13px" }}>{r.rank}</span>
            <div className="av" style={{ width: "32px", height: "32px", background: COLOURS[(r.rank - 1) % COLOURS.length], fontSize: "10px", flex: "none" }}>{initials(r.name)}</div>
            <span style={{ flex: "1", minWidth: "0", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
            <span className="mono muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>{r.score}{unit(board)}</span>
            <span style={{ width: "62px", textAlign: "right", ...MONO, fontSize: "12px", color: "#CCFF00" }}>{money(r.prize)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function App23_Leaderboard() {
  const { me, now } = useGame();
  useTicker(30_000);
  const [tab, setTab] = useState<Board | "last">(() => (sessionStorage.getItem("dd:ranksTab") as Board | "last" | null) ?? "xp");
  const [views, setViews] = useState<Partial<Record<Board, View>>>({});
  const [last, setLast] = useState<Partial<Record<Board, Last>>>({});
  useEffect(() => {
    sessionStorage.setItem("dd:ranksTab", tab);
    if (tab === "last") {
      for (const b of ["xp", "volume"] as const) void api<Last>(`/v1/ranks?board=${b}&week=last`).then((l) => setLast((s) => ({ ...s, [b]: l }))).catch(() => {});
      return;
    }
    const load = () => void api<View>(`/v1/ranks?board=${tab}`).then((v) => setViews((s) => ({ ...s, [tab]: v }))).catch(() => {});
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [tab]);

  const v = tab === "last" ? null : views[tab];
  const left = v ? Math.max(0, v.week.endsAt - now()) : 0;
  const ends = `${Math.floor(left / 86_400_000)}d ${String(Math.floor((left % 86_400_000) / 3_600_000)).padStart(2, "0")}h`;
  const myNote = (b: Board, m: NonNullable<View["me"]>) =>
    !m.qualified
      ? `${Math.max(0, 10 - m.battles)} more battles${m.opponents < 3 ? `, ${3 - m.opponents} more opponents` : ""} to place`
      : m.toTop10 ? `${m.toTop10}${b === "xp" ? " XP" : " USDT staked"} to the top 10` : m.rank && m.rank <= 10 ? "In the money" : `${m.battles} battles`;

  return (
    <>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "40px", left: "-45px", width: "480px", height: "480px" }}></div>
  <div className="pad" style={{ height: "100%", boxSizing: "border-box", paddingBottom: "0", display: "flex", flexDirection: "column", overflow: "hidden" }}>
    <div className="top" style={{ justifyContent: "center" }}><RanksEventsToggle on="ranks" /></div>
    <div className="tabs g" role="tablist" style={{ marginTop: "0", gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
      {([["xp", "Weekly XP"], ["volume", "High rollers"], ["last", "Last week"]] as const).map(([k, label]) => (
        <button key={k} role="tab" aria-selected={tab === k} className={`tab${tab === k ? " on" : ""}`} onClick={() => setTab(k)}>{label}</button>
      ))}
    </div>

    {tab !== "last" && (
      <>
        <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderRadius: "16px", background: "linear-gradient(90deg, rgba(204,255,0,0.16), rgba(255,51,102,0.12))", border: "1px solid rgba(204,255,0,0.45)" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"></path><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"></path></svg>
          <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column" }}>
            <span style={{ ...UN, fontSize: "11px", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>PRIZE POT ${v?.pot ?? "..."}</span>
            <span style={{ fontSize: "11px", color: "#8B949E" }}>{rules().weekly.potPct}% of this week's fees · top 10 paid</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flex: "none" }}><span style={{ fontSize: "10px", color: "#8B949E" }}>ENDS IN</span><span style={{ ...MONO, fontSize: "13px", color: "#CCFF00" }}>{v ? ends : "..."}</span></div>
        </div>
      </>
    )}
    {/* Only this part scrolls: the tabs and the prize pot stay put, and your row is pinned above the menu. */}
    <div className="fit" style={{ flex: "1", minHeight: "0", height: "auto", paddingBottom: "calc(var(--dd-nav-bottom) + 150px)" }}>
    {tab !== "last" && (
      <>
        {v && v.top.length === 0 && <p className="muted" style={{ marginTop: "24px", textAlign: "center", fontSize: "13px", lineHeight: "19px" }}>Nobody has placed yet this week. 10 battles against 3 different opponents puts you on the board.</p>}
        {v && v.top.length > 0 && <Podium top={v.top} board={tab} />}
        {v && <List rows={v.top.slice(3)} board={tab} />}
      </>
    )}

    {tab === "last" && (
      <>
        {!last.xp?.week && <p className="muted" style={{ marginTop: "24px", textAlign: "center", fontSize: "13px" }}>The first week's results show here once it ends, Monday 00:00 WAT.</p>}
        {(["xp", "volume"] as const).map((b) => last[b]?.week && (
          <div key={b} style={{ marginTop: "16px" }}>
            <span className="lbl">{b === "xp" ? "WEEKLY XP" : "HIGH ROLLERS"} · WEEK OF {last[b]!.week}</span>
            {last[b]!.top.length ? <List rows={last[b]!.top} board={b} /> : <p className="muted" style={{ fontSize: "13px" }}>Nobody qualified.</p>}
          </div>
        ))}
      </>
    )}
    </div>
  </div>

  {tab !== "last" && v?.me && (
    <div style={{ position: "absolute", left: "12px", right: "12px", bottom: "calc(var(--dd-nav-bottom) + 72px)", zIndex: 6, borderRadius: "18px", background: "rgba(22,32,10,0.96)", border: "1px solid rgba(204,255,0,0.5)", display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", backdropFilter: "blur(12px)" }}>
      <span className="mono lime" style={{ width: "28px", fontSize: "13px", fontWeight: "700", flex: "none" }}>{v.me.rank ?? "–"}</span>
      <div className="av" style={{ width: "32px", height: "32px", background: "#CCFF00", fontSize: "10px", flex: "none" }}>{initials(me?.user.username ?? me?.user.firstName ?? "ME")}</div>
      <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: "14px", fontWeight: "600" }}>You</span>
        <span style={{ fontSize: "11px", color: "#8B949E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{myNote(tab as Board, v.me)}</span>
      </div>
      <span className="mono lime" style={{ fontSize: "13px", fontWeight: "700", whiteSpace: "nowrap" }}>{v.me.score}{unit(tab as Board)}</span>
    </div>
  )}

  <nav className="nav g" aria-label="Main">
    <a href="#/App09"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 17.5 L3 6V3h3l11.5 11.5"></path><path d="M13 19l6-6M16 16l4 4M19 21l2-2"></path></svg>Arena</a>
    <a href="#/App23" className="on"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"></path><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"></path></svg>Ranks</a>
    <a href="#/App25"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>History</a>
    <a href="#/App24"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="4"></circle><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"></path></svg>Profile</a>
  </nav>
</div>
    </>
  );
}
